<script setup lang="ts">
import { Building2, Calendar, Clock, Tag } from '@lucide/vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { Badge } from '@/components/ui/badge'

import { useCommunicationFormatters } from '../../composables/useCommunicationFormatters'
import type { SubtypeHeaderPayload } from '../../types/communication'

const props = defineProps<{
  payload: SubtypeHeaderPayload
  /** i18n namespace the `label_key`s resolve under. Defaults to user-notice. */
  translationPrefix?: string
}>()

const { t, te } = useI18n()
const { formatFullDate } = useCommunicationFormatters()

const prefix = computed(() => props.translationPrefix ?? 'user-notice')

function translateLabel(labelKey: string): string {
  const nested = `models.${prefix.value}.fields.app_data.${labelKey}`
  if (te(nested)) return t(nested)
  const flat = `models.${prefix.value}.fields.${labelKey}`
  if (te(flat)) return t(flat)
  return labelKey
}

function translateBadge(fieldKey: string, value: string | number | null): string {
  if (value === null || value === '') return '—'
  const strVal = String(value)
  const nested = `models.${prefix.value}.options.app_data.${fieldKey}.${strVal}`
  if (te(nested)) return t(nested)
  const flat = `models.${prefix.value}.options.${fieldKey}.${strVal}`
  if (te(flat)) return t(flat)
  const badge = `models.${prefix.value}.badges.${strVal}`
  if (te(badge)) return t(badge)
  return strVal
}

function formatValue(field: SubtypeHeaderPayload['fields'][number]): string {
  if (field.value === null || field.value === '') return '—'
  if (field.type === 'date') return formatFullDate(String(field.value))
  if (field.type === 'badge') return translateBadge(field.label_key, field.value)
  return String(field.value)
}

const toneClasses = computed(() => {
  const color = props.payload.color
  switch (color) {
    case 'negative':
      return 'bg-destructive/10 text-destructive ring-destructive/20'
    case 'warning':
      return 'bg-amber-500/10 text-amber-700 ring-amber-500/20 dark:text-amber-400'
    case 'info':
    default:
      return 'bg-primary/10 text-primary ring-primary/20'
  }
})

function iconFor(fieldType: string) {
  switch (fieldType) {
    case 'date':
      return Calendar
    case 'client':
      return Building2
    case 'badge':
      return Tag
    default:
      return Clock
  }
}
</script>

<template>
  <div v-if="payload.fields.length > 0" class="rounded-lg p-3 ring-1" :class="toneClasses">
    <div class="flex flex-wrap items-center gap-2">
      <Badge variant="secondary" class="uppercase tracking-wider">
        {{ payload.type_label }}
      </Badge>
      <div class="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1">
        <div
          v-for="field in payload.fields"
          :key="field.label_key"
          class="flex items-center gap-1.5 text-sm"
        >
          <component :is="iconFor(field.type)" class="size-3.5 opacity-70" />
          <span class="font-medium text-foreground/80">{{ translateLabel(field.label_key) }}:</span>
          <span class="text-foreground">{{ formatValue(field) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
